import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import type { AgentTask } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { agentTaskInvalidationHolders, useAgentTaskInvalidation } from './agent-task-invalidation';
import { channelListenerCount } from './duplexer-connection';
import { queryKeys } from './query-client';
import { noteFetch } from './runaway-fetch-probe';
import { agentTasksAreEmpty, bucketOfTask, bucketTasks, type AgentTaskBucket } from './agent-task-buckets';

// Client data layer for the workspace "Agents at work" screen. One fetch to
// /api/workspace-tasks?workspaceId= returns the workspace's (and its child projects') raw v2 AgentTasks;
// the card renders off the AgentTask's `title` / `status` / `display`.
//
// Bucketing and the recency window come from lib/agent-task-buckets — the SAME source the Agent Tasks
// screen these cards link to uses, so a card's count is a true promise about what that tab contains.

/**
 * The raw AgentTask plus the two fields `/api/workspace-tasks` decorates it with. They are the counts'
 * business, not the card's: `bucketOfTask` promotes a task holding an OPEN question into `waiting`
 * whatever its status says, and these stat cards and the island's header count both bucket through it.
 * Without them this aggregate would count a blocked task as In progress while the Tasks screen — reading
 * the same tasks off `/api/tasks` — lists it under Action needed.
 */
export type WorkspaceTask = AgentTask & {
  /** Absent or non-empty, never `[]`. */
  decisionPrompts?: { decisionId: string }[];
  /** True or absent, never `false`. Deliberately not part of the promotion — see `awaitsDecision`. */
  decisionPromptsUnavailable?: boolean;
};

export type WorkspaceTasksState = { status: 'loading' } | { status: 'ready'; tasks: WorkspaceTask[] } | { status: 'error' };

/** The card row shows the three primary states; a failed task is surfaced by the Agent Tasks Failed tab. */
export type WorkspaceTaskBucket = Extract<AgentTaskBucket, 'waiting' | 'inProgress' | 'done'>;

export const bucketOf = bucketOfTask;

/**
 * A settled workspace with nothing at all in the window — the Agents-at-work Tasks section hides its
 * "All tasks" link and shows the empty state instead of zero-count cards (Figma), matching the native tab.
 *
 * Counts every bucket, INCLUDING failed: a workspace whose only tasks failed still has tasks, and calling
 * it empty here would contradict the Failed tab that "All tasks" opens.
 */
export const workspaceTasksAreEmpty = agentTasksAreEmpty;

/** The windowed per-bucket split the stat cards count from. */
export function bucketWorkspaceTasks(tasks: WorkspaceTask[]): Record<AgentTaskBucket, WorkspaceTask[]> {
  return bucketTasks(tasks);
}

export async function fetchWorkspaceTasks(workspaceId: string): Promise<WorkspaceTask[]> {
  // Instrumented for the SAME reason `fetchTasks` is, and it was the half of the pair that was missing.
  // The probe reports per `label`, so an uninstrumented fetcher does not read as "quiet" — it CANNOT
  // EMIT AT ALL, and an earlier reader of the report counts nearly concluded from the absence of
  // `label=workspace-tasks` that this route was not storming. Per-IP-minute access logs said the
  // opposite: it runs at roughly 3x the request rate of `/api/tasks`. Absence of instrumentation must
  // never be readable as absence of a problem.
  //
  // `subs` is the duplexer channel's fan-out and `holders` the count of hooks retaining the ONE shared
  // agent-task listener — together they say whether a burst came through the transport at all, which is
  // exactly the question the fix in `lib/agent-task-invalidation.ts` changes the answer to.
  noteFetch(
    'workspace-tasks',
    workspaceId || 'unscoped',
    () =>
      `subs=${channelListenerCount(IndividualsDuplexerChannel.AGENT_TASK)} holders=${agentTaskInvalidationHolders()}`,
  );
  const res = await apiFetch(`${API_BASE}/api/workspace-tasks?workspaceId=${encodeURIComponent(workspaceId)}`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { tasks?: WorkspaceTask[] };
  return data.tasks ?? [];
}

export function useWorkspaceTasks(workspaceId: string | undefined): WorkspaceTasksState {
  const query = useQuery({
    queryKey: queryKeys.workspaceTasks(workspaceId),
    queryFn: () => fetchWorkspaceTasks(workspaceId as string),
    enabled: !!workspaceId,
  });

  // AgentTask created/progressed/deleted by chat refreshes the list live — via the ONE shared listener
  // that invalidates the `workspace-tasks` family, so several mounted cards cost one refetch per entry
  // rather than one per card. A card with no workspace has a disabled query, which invalidation leaves
  // alone: the `if (workspaceId)` guard this replaces is now structural.
  useAgentTaskInvalidation();

  // Keep a prior good list on a transient refetch failure — only surface an error before the first success.
  if (query.data) return { status: 'ready', tasks: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}
